import React, { useCallback, useMemo } from "react";
import { Feature, Point } from "geojson";
import { FeatureIdentifier } from "mapbox-gl";

import { inAddress } from "fond/layers";
import { DeleteButton, PointButton, PolygonSelectButton, RedoButton, SelectButton, ToolbarConfig, UndoButton } from "fond/map/Toolbar";
import { AddressIcon } from "fond/svg_icons";
import { useAppSelector } from "fond/utils/hooks";

import DrawHandler from "../DrawHandler";

import addressDrawStyles from "./addressDrawStyles";

const defaultOptions = {
  snap: false,
};

const sources: FeatureIdentifier[] = [{ source: inAddress }];

// Base drawing point tools without snapping
const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<PointButton key="drawPoint" icon={<AddressIcon />} title="Draw address" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

/**
 * This component is responsible for managing the drawing of demand points.
 * It uses the Mapbox Draw library to provide drawing functionality and
 * manages the state of the drawn features.
 */
const AddressHandler: React.FC = () => {
  const { data } = useAppSelector((state) => state.project.projects[state.project.projectId]);

  // The initial features are derived from the data layer for addresses which
  // are drawn as Points.
  const initialFeatures: Feature<Point>[] = useMemo(() => (data?.layers[inAddress].features || []) as Feature<Point>[], [data]);

  /**
   * Determines the initial properties for any new feature being drawn.
   */
  const getFeatureProperties = useCallback((_: Feature) => ({ NumFibers: 1, SubTypes: null, Type: "T1_DEMAND", layerId: inAddress }), []);

  return (
    <DrawHandler
      startingMode={initialFeatures.length > 0 ? "simple_select" : "draw_point"}
      initialFeatures={initialFeatures}
      sources={sources}
      styles={addressDrawStyles}
      config={toolbarConfig}
      getFeatureProperties={getFeatureProperties}
      options={defaultOptions}
    />
  );
};

export default AddressHandler;
